import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useSelector } from 'react-redux';
import QrScanner from '../../../lib/agicashQrScanner';
import type { ScanResult } from '../../../lib/agicashQrScanner';
import strings from '../../../localization';
import { chromeSecondaryButtonClassName } from '../../../constants/chromeSecondaryButton';
import { stateType } from '../../../types/types';

interface QrScannerCameraProps {
  className?: string;
  onResult: (data: string) => void;
  onInitError: (message: string) => void;
}

type CameraStatus = 'idle' | 'starting' | 'active' | 'error';

const QR_SCANNER_WORKER_URL = `${process.env.PUBLIC_URL || ''}/qr-scanner-worker.js`;
const NO_QR_FOUND_MESSAGE = 'No QR code found';

let workerUrlReady = false;

// Points the WASM worker at a static URL for CRA/webpack 4.
function ensureWorkerUrl(): void {
  if (workerUrlReady) {
    return;
  }

  QrScanner.setWorkerUrl(QR_SCANNER_WORKER_URL);
  workerUrlReady = true;
}

// Scans the entire camera frame at native resolution (no center crop).
function calculateFullFrameScanRegion(video: HTMLVideoElement) {
  const width = video.videoWidth;
  const height = video.videoHeight;

  if (!width || !height) {
    return { x: 0, y: 0, width: 1, height: 1 };
  }

  return { x: 0, y: 0, width, height };
}

// Maps camera errors to localized user-facing messages.
function getCameraErrorMessage(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === 'CameraPermissionError') {
      return strings.selfTest.cameraPermissionDenied;
    }
    if (error.name === 'CameraNotFoundError') {
      return strings.selfTest.cameraNotFound;
    }
  }

  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      return strings.selfTest.cameraPermissionDenied;
    }
    if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      return strings.selfTest.cameraNotFound;
    }
  }

  if (error instanceof Error && (error as Error & { code?: string }).code === 'insecure') {
    return strings.selfTest.cameraHttpsRequired;
  }

  return strings.selfTest.cameraStartFailed;
}

// Renders the camera stream and decodes QR codes via @agicash/qr-scanner (ZXing WASM).
const QrScannerCamera: FC<QrScannerCameraProps> = ({ className = '', onResult, onInitError }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const scannerRef = useRef<InstanceType<typeof QrScanner> | null>(null);
  const onResultRef = useRef(onResult);
  const onInitErrorRef = useRef(onInitError);
  const isMountedRef = useRef(true);
  const requiresUserGestureRef = useRef(isMobile);

  const [status, setStatus] = useState<CameraStatus>(isMobile ? 'idle' : 'starting');
  const [showEnableButton, setShowEnableButton] = useState(isMobile);
  const statusRef = useRef<CameraStatus>(status);
  const flashMessage = useSelector((state: stateType) => state.app.flashMessage);
  const hadFlashMessageRef = useRef(false);

  statusRef.current = status;

  useEffect(() => {
    ensureWorkerUrl();
    void QrScanner.preload();
  }, []);

  useEffect(() => {
    onResultRef.current = onResult;
    onInitErrorRef.current = onInitError;
  }, [onResult, onInitError]);

  // Restarts preview and decode when the browser or a modal pauses the video track.
  const keepScannerAlive = useCallback(() => {
    const scanner = scannerRef.current;
    const video = videoRef.current;

    if (!scanner || !video || !isMountedRef.current || statusRef.current !== 'active') {
      return;
    }

    void scanner.start().catch(() => undefined);

    if (video.paused) {
      void video.play().catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    if (hadFlashMessageRef.current && !flashMessage) {
      keepScannerAlive();
    }

    hadFlashMessageRef.current = Boolean(flashMessage);
  }, [flashMessage, keepScannerAlive]);

  // Stops the scanner and releases the active camera stream.
  const stopScanner = useCallback(() => {
    scannerRef.current?.destroy();
    scannerRef.current = null;
  }, []);

  // Requests camera access and starts QR decoding.
  const startCamera = useCallback(async () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    ensureWorkerUrl();
    setStatus('starting');
    setShowEnableButton(false);
    stopScanner();

    try {
      const scanner = new QrScanner(
        video,
        (result: ScanResult) => {
          const payload = result.data?.trim();
          if (!payload) {
            return;
          }

          onResultRef.current(payload);
          keepScannerAlive();
          window.setTimeout(keepScannerAlive, 0);
        },
        {
          preferredCamera: 'environment',
          maxScansPerSecond: 15,
          calculateScanRegion: calculateFullFrameScanRegion,
          highlightScanRegion: false,
          highlightCodeOutline: false,
          cameraResolution: {
            width: { ideal: 1920, min: 1280 },
            height: { ideal: 1080, min: 720 },
          },
          decoderOptions: {
            tryHarder: true,
            tryInvert: true,
            tryDownscale: true,
            tryRotate: true,
          },
          onDecodeError: (error) => {
            if (error === NO_QR_FOUND_MESSAGE) {
              return;
            }

            console.warn('QR decode error:', error);
          },
        },
      );

      scanner.setInversionMode('both');
      scannerRef.current = scanner;
      await scanner.start();

      if (!isMountedRef.current) {
        scanner.destroy();
        return;
      }

      setStatus('active');
    } catch (error) {
      stopScanner();

      if (!isMountedRef.current) {
        return;
      }

      setStatus('error');
      setShowEnableButton(true);
      onInitErrorRef.current(getCameraErrorMessage(error));
    }
  }, [keepScannerAlive, stopScanner]);

  useEffect(() => {
    isMountedRef.current = true;

    if (!requiresUserGestureRef.current) {
      startCamera();
    }

    return () => {
      isMountedRef.current = false;
      stopScanner();
    };
  }, [startCamera, stopScanner]);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || status !== 'active') {
      return undefined;
    }

    const handleVideoPause = () => {
      keepScannerAlive();
    };

    const handleMetadata = () => {
      keepScannerAlive();
    };

    video.addEventListener('pause', handleVideoPause);
    video.addEventListener('loadedmetadata', handleMetadata);

    return () => {
      video.removeEventListener('pause', handleVideoPause);
      video.removeEventListener('loadedmetadata', handleMetadata);
    };
  }, [keepScannerAlive, status]);

  const isStarting = status === 'starting';

  return (
    <div className={`qr-scan__scanner ${className}`.trim()}>
      <section className="qr-scan__scanner-stage">
        <video ref={videoRef} className="qr-scan__scanner-video" playsInline muted autoPlay />

        <div className="qr-scan__scanner-frame" aria-hidden="true">
          <span className="qr-scan__scanner-corner qr-scan__scanner-corner--tl" />
          <span className="qr-scan__scanner-corner qr-scan__scanner-corner--tr" />
          <span className="qr-scan__scanner-corner qr-scan__scanner-corner--bl" />
          <span className="qr-scan__scanner-corner qr-scan__scanner-corner--br" />
        </div>

        {showEnableButton && (
          <div className="qr-scan__scanner-overlay">
            <button
              type="button"
              className={chromeSecondaryButtonClassName}
              onClick={startCamera}
              disabled={isStarting}
            >
              {isStarting ? strings.selfTest.cameraStarting : strings.selfTest.cameraEnableButton}
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default QrScannerCamera;
